<template>
  <BContainer
    class="p-0"
    fluid
  >
    <ValidationObserver
      ref="formChangePassword"
    >
      <form
        novalidate
        role="form"
        @submit.prevent
      >
        <BRow>
          <BCol
            cols="12"
            md="4"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="current_password"
              name="current password"
              rules="required"
            >
              <BFormGroup
                label="Current Password"
                label-for="current_password"
                label-class="font-weight-bold"
                class="mb-2"
              >
                <BInputGroup
                  :class="errors.length > 0 ? 'is-invalid' : null"
                >
                  <BFormInput
                    id="current_password"
                    v-model="credentials.current_password"
                    placeholder="enter current password"
                    autocomplete="off"
                    :type="currentPasswordType"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <BInputGroupAppend>
                    <BButton
                      class="pl-2 pr-1 text-dark input-icon"
                      variant="warning"
                      :disabled="state.busy"
                      @click.prevent="toggleCurrentPassword"
                    >
                      <i
                        v-if="state.current_password_visible"
                        class="fad fa-eye h6 mb-0 min-w-40px"
                      />
                      <i
                        v-else
                        class="fad fa-eye-slash h6 mb-0 min-w-40px"
                      />
                    </BButton>
                  </BInputGroupAppend>
                </BInputGroup>
                <div
                  v-if="errors.length > 0"
                  class="invalid-feedback"
                >
                  <span>{{ errors[0] }}</span>
                </div>
              </BFormGroup>
            </ValidationProvider>
          </BCol>

          <BCol
            cols="12"
            md="4"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="password"
              name="new password"
              :rules="{
                required: true,
                not_equal: 'current_password'
              }"
            >
              <BFormGroup
                label="New Password"
                label-for="password"
                label-class="font-weight-bold"
                class="mb-2"
              >
                <BInputGroup
                  :class="errors.length > 0 ? 'is-invalid' : null"
                >
                  <BFormInput
                    id="password"
                    v-model="credentials.password"
                    placeholder="enter new password"
                    autocomplete="off"
                    :type="newPasswordType"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <BInputGroupAppend>
                    <BButton
                      class="pl-2 pr-1 text-dark input-icon"
                      variant="warning"
                      :disabled="state.busy"
                      @click.prevent="toggleNewPassword"
                    >
                      <i
                        v-if="state.new_password_visible"
                        class="fad fa-eye h6 mb-0 min-w-40px"
                      />
                      <i
                        v-else
                        class="fad fa-eye-slash h6 mb-0 min-w-40px"
                      />
                    </BButton>
                  </BInputGroupAppend>
                </BInputGroup>
                <div
                  v-if="errors.length > 0"
                  class="invalid-feedback"
                >
                  <span>{{ errors[0] }}</span>
                </div>
              </BFormGroup>
            </ValidationProvider>
          </BCol>

          <BCol
            cols="12"
            md="4"
          >
            <ValidationProvider
              v-slot="{ errors }"
              name="confirm password"
              vid="password_confirmation"
              rules="required|min:8|confirmed:password"
            >
              <BFormGroup
                label="Confirm New Password"
                label-for="password_confirmation"
                label-class="font-weight-bold"
                class="mb-2"
              >
                <BInputGroup
                  :class="errors.length > 0 ? 'is-invalid' : null"
                >
                  <BFormInput
                    id="password_confirmation"
                    v-model="credentials.password_confirmation"
                    placeholder="confirm new password"
                    autocomplete="off"
                    :type="newPasswordType"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <BInputGroupAppend>
                    <BButton
                      class="pl-2 pr-1 text-dark input-icon"
                      variant="warning"
                      :disabled="state.busy"
                      @click.prevent="toggleNewPassword"
                    >
                      <i
                        v-if="state.new_password_visible"
                        class="fad fa-eye h6 mb-0 min-w-40px"
                      />
                      <i
                        v-else
                        class="fad fa-eye-slash h6 mb-0 min-w-40px"
                      />
                    </BButton>
                  </BInputGroupAppend>
                </BInputGroup>
                <div
                  v-if="errors.length > 0"
                  class="invalid-feedback"
                >
                  <span>{{ errors[0] }}</span>
                </div>
              </BFormGroup>
            </ValidationProvider>
          </BCol>
        </BRow>

        <BRow
          v-if="requiresPasswordOtp"
          class="mb-2"
        >
          <BCol cols="12">
            <div class="p-2 border rounded bg-light">
              <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <strong>Email OTP Verification</strong>
                  <p class="small text-muted mb-0">
                    Verify your account email before changing your default password.
                  </p>
                </div>

                <BButton
                  size="sm"
                  variant="outline-success"
                  :disabled="state.busy || otp.sending || otp.cooldown > 0"
                  @click.prevent="sendPasswordOtp"
                >
                  <i :class="['fas me-1', otp.sending ? 'fa-spinner fa-spin' : 'fa-paper-plane']" />
                  {{ otp.sending ? 'Sending OTP...' : 'Send OTP' }}
                </BButton>
              </div>

              <small
                v-if="otp.cooldown > 0"
                class="text-muted d-block mt-2"
              >
                Resend available in {{ otp.cooldown }}s
              </small>

              <div
                v-if="otp.sent && !otp.verified"
                class="d-flex align-items-center gap-2 flex-wrap mt-2"
              >
                <BFormInput
                  v-model.trim="otp.code"
                  type="text"
                  maxlength="6"
                  placeholder="Enter 6-digit OTP"
                  :disabled="state.busy || otp.verifying"
                  style="max-width: 220px;"
                />
                <BButton
                  size="sm"
                  variant="success"
                  :disabled="state.busy || otp.verifying || otp.code.length !== 6"
                  @click.prevent="verifyPasswordOtp"
                >
                  <i :class="['fas me-1', otp.verifying ? 'fa-spinner fa-spin' : 'fa-check']" />
                  {{ otp.verifying ? 'Verifying...' : 'Verify OTP' }}
                </BButton>
              </div>

              <small
                v-if="otp.success"
                class="text-success d-block mt-2"
              >
                <i class="fas fa-check-circle me-1" />
                {{ otp.success }}
              </small>

              <small
                v-if="otp.error"
                class="text-danger d-block mt-2"
              >
                <i class="fas fa-exclamation-circle me-1" />
                {{ otp.error }}
              </small>
            </div>
          </BCol>
        </BRow>

        <div class="d-flex justify-content-end w-100">
          <BButton
            variant="primary"
            :disabled="state.busy"
            @click.prevent="onValidateChangePassword"
          >
            Update Password
          </BButton>
        </div>
      </form>
    </ValidationObserver>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import MxMisc from '@/mixins/misc'
import { SStudentAuth } from '@/services/student'

export default {
  name: 'VStudentChangePassword',

  mixins: [MxMisc],

  middleware: ['auth', 'student'],

  metaInfo: () => ({
    title: 'Change Password'
  }),

  data () {
    return {
      state: {
        busy: false,
        current_password_visible: false,
        new_password_visible: false
      },
      otpTimerId: null,
      otp: {
        sending: false,
        sent: false,
        verifying: false,
        verified: false,
        code: '',
        token: '',
        cooldown: 0,
        success: '',
        error: ''
      },
      credentials: {
        current_password: '',
        password: '',
        password_confirmation: '',
        otp_verification_token: ''
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    requiresPasswordOtp () {
      return Boolean(this.currentUser?.must_change_password)
    },

    currentPasswordType () {
      return this.state.current_password_visible ? 'text' : 'password'
    },

    newPasswordType () {
      return this.state.new_password_visible ? 'text' : 'password'
    }
  },

  beforeDestroy () {
    this.clearOtpTimer()
  },

  methods: {
    clearOtpTimer () {
      if (this.otpTimerId) {
        clearInterval(this.otpTimerId)
        this.otpTimerId = null
      }
    },

    startOtpCooldown (seconds = 60) {
      this.clearOtpTimer()
      this.otp.cooldown = seconds

      this.otpTimerId = setInterval(() => {
        this.otp.cooldown = Math.max(0, this.otp.cooldown - 1)
        if (this.otp.cooldown === 0) {
          this.clearOtpTimer()
        }
      }, 1000)
    },

    resetOtpState () {
      this.otp.sent = false
      this.otp.verifying = false
      this.otp.verified = false
      this.otp.code = ''
      this.otp.token = ''
      this.otp.success = ''
      this.otp.error = ''
      this.credentials.otp_verification_token = ''
      this.clearOtpTimer()
      this.otp.cooldown = 0
    },

    async sendPasswordOtp () {
      this.otp.error = ''
      this.otp.success = ''
      this.otp.sending = true

      try {
        const { data } = await SStudentAuth.sendPasswordChangeOtp()
        this.otp.sent = true
        this.otp.verified = false
        this.otp.code = ''
        this.otp.token = ''
        this.credentials.otp_verification_token = ''
        this.otp.success = data?.message || 'OTP sent successfully.'
        this.startOtpCooldown(60)
      } catch (error) {
        this.otp.error = error?.response?.data?.message || 'Failed to send OTP.'
      } finally {
        this.otp.sending = false
      }
    },

    async verifyPasswordOtp () {
      this.otp.error = ''
      this.otp.success = ''

      const code = (this.otp.code || '').trim()
      if (!/^\d{6}$/.test(code)) {
        this.otp.error = 'Please enter the 6-digit OTP.'
        return
      }

      this.otp.verifying = true

      try {
        const { data } = await SStudentAuth.verifyPasswordChangeOtp(code)
        this.otp.verified = true
        this.otp.token = data?.data?.verification_token || ''
        this.credentials.otp_verification_token = this.otp.token
        this.otp.success = data?.message || 'OTP verified successfully.'
      } catch (error) {
        this.otp.verified = false
        this.otp.token = ''
        this.credentials.otp_verification_token = ''
        this.otp.error = error?.response?.data?.message || 'Invalid OTP.'
      } finally {
        this.otp.verifying = false
      }
    },

    toggleCurrentPassword () {
      this.state.current_password_visible = !this.state.current_password_visible
    },

    toggleNewPassword () {
      this.state.new_password_visible = !this.state.new_password_visible
    },

    async onValidateChangePassword (event) {
      event.preventDefault()
      await this.$refs.formChangePassword.validate().then(
        async validForm => {
          if (!validForm) {
            return this.swalInvalid()
          }

          if (this.requiresPasswordOtp && !(this.otp.verified && this.otp.token)) {
            this.otp.error = 'Please verify OTP before changing your password.'
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: '<h6>Update your Password?</h6><p class="small text-muted">You will be logged out and need to sign in again.</p>',
            confirmButtonText: 'Update',
            preConfirm: () => {
              this.state.busy = true
              return this.onPostChangePassword()
            }
          })
        }
      )
    },

    async onPostChangePassword () {
      return new Promise(resolve => {
        const payload = {
          current_password: this.credentials.current_password,
          password: this.credentials.password,
          password_confirmation: this.credentials.password_confirmation,
          otp_verification_token: this.requiresPasswordOtp ? this.credentials.otp_verification_token : null
        }

        SStudentAuth.changePassword(payload).then(
          ({ data: { message } }) => {
            this.resetOtpState()
            this.swalSuccess(message || 'Password updated. Please login again.').then(
              () => {
                this.$store.dispatch('auth/logout')
                this.$router.replace({ name: 'shared.login' })
              }
            )
          }
        ).catch(
          ({ message }) => {
            this.$refs.formChangePassword.setErrors(message.errors)
            resolve(message)
          }
        ).finally(() => {
          this.state.busy = false
        })
      })
    }
  }
}
</script>
